import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../../shared/components/UIElements/Modal";
import Input from "../../shared/components/FormElements/Input";
import Button from "../../shared/components/FormElements/Button";
import { VALIDATOR_EMAIL, VALIDATOR_REQUIRE } from "../../shared/util/validators";
import { isPhoneDigitLengthValid } from "../../shared/util/contactInfo";
import { createDemoRequest } from "../../help/services/supportService";
import "./styling/DemoRequestModal.css";

const DEMO_EMAIL = "service@fortedynamics.com";

const REQUIRED = ["name", "email", "company", "role", "times"];

const emptyForm = () => ({
  name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  times: "",
  notes: "",
});

/**
 * DemoRequestModal — collects a prospect's contact details and preferred demo
 * times and files them as a `demo` support request (POST
 * /v1/support-request/demo, no sign-in needed). Admins who opted in are
 * emailed and the request appears in Admin → Support. If the request cannot
 * be sent, the prospect is offered a direct email instead.
 */
export default function DemoRequestModal({ show, onCancel }) {
  const data = useRef(emptyForm());
  const valid = useRef({});
  const [formValid, setFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // Remounting the form after a successful send clears the shared Inputs,
  // which keep their own internal state.
  const [formKey, setFormKey] = useState(0);
  // Honeypot — real users never see or fill this; bots that do get a silent
  // "success" from Maestro and nothing is stored.
  const honeypot = useRef(null);

  const recompute = useCallback(() => {
    const d = data.current;
    const v = valid.current;
    const requiredOk = REQUIRED.every((k) => d[k].trim() && v[k] !== false);
    const phoneOk = !d.phone.trim() || isPhoneDigitLengthValid(d.phone);
    setFormValid(requiredOk && phoneOk);
  }, []);

  const handleInput = useCallback(
    (id, value, isValid) => {
      data.current[id] = value;
      valid.current[id] = isValid;
      recompute();
    },
    [recompute],
  );

  useEffect(() => {
    if (!show) return;
    // Fresh modal each open: a prospect who closed the thank-you should be
    // able to file another (for a colleague, say) without stale state.
    setError(null);
    setSubmitted(false);
    setSubmitting(false);
  }, [show]);

  const submit = async (event) => {
    event.preventDefault();
    if (!formValid || submitting) return;
    const d = data.current;
    setSubmitting(true);
    setError(null);
    try {
      await createDemoRequest({
        name: d.name.trim(),
        email: d.email.trim(),
        phone: d.phone.trim(),
        company: d.company.trim(),
        role: d.role.trim(),
        preferredTimes: d.times.trim(),
        notes: d.notes.trim(),
        website: honeypot.current?.value || "",
      });
      setSubmitted(true);
      data.current = emptyForm();
      valid.current = {};
      setFormValid(false);
      setFormKey((k) => k + 1);
    } catch (err) {
      setError(
        err?.message ||
          "We couldn't send your request just now. Please try again or email us directly.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const mailtoFallback =
    `mailto:${DEMO_EMAIL}?subject=` +
    encodeURIComponent("Orecasting Demo Request");

  return (
    <Modal
      show={show}
      onCancel={onCancel}
      header="Request a Demo"
      contentClass="demo-modal__content"
      footer={
        submitted ? (
          <div className="demo-modal__actions">
            <Button onClick={onCancel}>Close</Button>
          </div>
        ) : (
          <div className="demo-modal__actions">
            <Button inverse onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="demo-request-form"
              disabled={!formValid || submitting}
            >
              {submitting ? "Sending…" : "Send Request"}
            </Button>
          </div>
        )
      }
    >
      {submitted ? (
        <div className="demo-modal__success" role="status">
          <span className="demo-modal__success-icon" aria-hidden="true">
            ✅
          </span>
          <div>
            <strong>Thanks — your demo request is in.</strong>
            <p>
              The Forte Dynamics team will reach out at the email you provided
              to schedule a time.
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="demo-modal__intro">
            Tell us a little about you and we&apos;ll reach out to schedule your
            demo.
          </p>
          <form
            id="demo-request-form"
            className="demo-form"
            onSubmit={submit}
            key={formKey}
          >
            <Input
              id="name"
              element="input"
              type="text"
              label="Name"
              validators={[VALIDATOR_REQUIRE()]}
              errorText="Please enter your name."
              onInput={handleInput}
            />
            <div className="demo-form__row">
              <Input
                id="email"
                element="input"
                type="email"
                label="Email"
                placeholder="you@company.com"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                errorText="Please enter a valid email address."
                onInput={handleInput}
              />
              <Input
                id="phone"
                element="input"
                type="tel"
                label="Phone (optional)"
                placeholder="+1 555 555 0100"
                onInput={handleInput}
              />
            </div>
            <div className="demo-form__row">
              <Input
                id="company"
                element="input"
                type="text"
                label="Company"
                validators={[VALIDATOR_REQUIRE()]}
                errorText="Please enter your company."
                onInput={handleInput}
              />
              <Input
                id="role"
                element="input"
                type="text"
                label="Role"
                validators={[VALIDATOR_REQUIRE()]}
                errorText="Please enter your role."
                onInput={handleInput}
              />
            </div>
            <Input
              id="times"
              element="input"
              type="text"
              label="Preferred times"
              placeholder="e.g. Weekday mornings (Mountain Time)"
              validators={[VALIDATOR_REQUIRE()]}
              errorText="Let us know when suits you."
              onInput={handleInput}
            />
            <Input
              id="notes"
              element="textarea"
              label="Anything else? (optional)"
              rows={3}
              onInput={handleInput}
            />
            {/* Honeypot: off-screen, skipped by tab order and screen readers. */}
            <div className="demo-form__hp" aria-hidden="true">
              <label htmlFor="demo-website">Website</label>
              <input
                id="demo-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                ref={honeypot}
              />
            </div>
            {error && (
              <div className="demo-modal__error" role="alert">
                {error}{" "}
                <a href={mailtoFallback}>Email {DEMO_EMAIL}</a>
              </div>
            )}
          </form>
        </>
      )}
    </Modal>
  );
}
